/* ==========================================================================
   Cart drawer helpers
   1. <cart-recommendations> — "You may also love" (up to 6, carousel with arrows) via the product
      recommendations endpoint: intent=complementary, falls back to related.
      Results cached per product so re-renders after qty changes don't refetch.
   2. Focus restore — theme.js swaps the drawer's innerHTML after cart changes;
      we put focus back on the equivalent control ([data-focus-key]).
   3. Re-render fallback — if the bundled section response did not include this
      drawer's section id, re-fetch it by file name ("cart-drawer").
   ========================================================================== */
const recCache = new Map();

if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    async connectedCallback() {
      const id = this.dataset.productId;
      if (!id) return;
      const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
      let cards = recCache.get(id);
      if (!cards) {
        cards = await this.load('complementary');
        if (!cards.length) cards = await this.load('related');
        recCache.set(id, cards);
      }
      if (!this.isConnected) return;
      const pick = cards.filter((c) => !exclude.has(c.handle)).slice(0, 6);
      if (!pick.length) { this.hidden = true; return; }
      const list = this.querySelector('[data-recs-list]');
      list.innerHTML = pick.map((c) => `<li>${c.html}</li>`).join('');
      this.hidden = false;
      this.carousel(list);
    }

    carousel(list) {
      const prev = this.querySelector('[data-recs-prev]');
      const next = this.querySelector('[data-recs-next]');
      const rtl = document.dir === 'rtl' ? -1 : 1;
      const step = () => list.clientWidth * 0.8 * rtl;
      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        const x = Math.abs(list.scrollLeft);
        if (prev) prev.disabled = x <= 2;
        if (next) next.disabled = x >= max - 2;
        this.querySelector('.cart-recs__nav')?.toggleAttribute('hidden', max <= 2);
      };
      prev?.addEventListener('click', () => list.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => list.scrollBy({ left: step(), behavior: 'smooth' }));
      list.addEventListener('scroll', update, { passive: true });
      // Recs often load while the drawer is closed (0 width): re-measure when it becomes visible / resizes.
      new ResizeObserver(update).observe(list);
      requestAnimationFrame(update);
    }

    async load(intent) {
      try {
        const u = new URL(this.dataset.url, location.origin);
        u.searchParams.set('product_id', this.dataset.productId);
        u.searchParams.set('limit', '6');
        u.searchParams.set('intent', intent);
        u.searchParams.set('section_id', this.dataset.sectionId || 'cart-recommendations');
        const res = await fetch(u);
        if (!res.ok) return [];
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        return Array.from(doc.querySelectorAll('[data-product-card]')).map((el) => ({
          handle: el.dataset.handle,
          html: el.outerHTML,
        }));
      } catch {
        return [];
      }
    }
  }
  customElements.define('cart-recommendations', CartRecommendations);
}

(() => {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer || drawer.dataset.enhanced) return;
  drawer.dataset.enhanced = '1';
  const wrapper = () => drawer.querySelector('[data-cart-section]');
  let lastKey = null;
  let stamp = drawer.querySelector('[data-cart-stamp]');

  drawer.addEventListener('focusin', (e) => {
    const k = e.target.closest('[data-focus-key]');
    lastKey = k ? k.dataset.focusKey : null;
  });

  const restoreFocus = () => {
    if (!drawer.open) return;
    if (drawer.contains(document.activeElement) && document.activeElement !== drawer) return;
    const target = (lastKey && drawer.querySelector(`[data-focus-key="${CSS.escape(lastKey)}"]`))
      || drawer.querySelector('[data-focus-key="close"]');
    target?.focus({ preventScroll: true });
  };

  const refetch = async () => {
    const w = wrapper();
    if (!w || !window.SIM?.fetchSectionHTML) return;
    try {
      const html = await window.SIM.fetchSectionHTML(location.pathname, 'cart-drawer');
      const fresh = window.SIM.parseHTML(html).querySelector('[data-cart-section]');
      if (fresh) w.innerHTML = fresh.innerHTML;
    } catch { /* keep current markup */ }
  };

  document.addEventListener('cart:updated', async () => {
    const current = drawer.querySelector('[data-cart-stamp]');
    if (current === stamp) await refetch(); // bundled render skipped us
    stamp = drawer.querySelector('[data-cart-stamp]');
    restoreFocus();
  });
})();
